import Button from '@sats-group/ui-lib/react/button';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input/hidden-input';
import Text from '@sats-group/ui-lib/react/text';
import React, { useEffect, useState } from 'react';
import ContentContainer from '~/client/components/content-container/content-container';
import MyPageLayout from '~/client/components/my-page-layout/my-page-layout';
import RadioCard from '~/client/components/radio-card/radio-card';
import Spinner from '~/client/components/spinner/spinner';
import type { ChangeMembershipOverviewPage as Props } from './change-membership-overview-page.types';

const ChangeMembershipOverviewPage: React.FunctionComponent<Props> = ({
  texts,
  myPageLayout,
  preSelectedGeoOption,
  preSelectedGXOption,
  comboOptions,
  nextButton,
  endpoint,
  hiddenInputs,
}) => {
  const [selectedGeoOption, setSelectedGeoOption] =
    useState(preSelectedGeoOption);
  const [selectedGXOption, setSelectedGXOption] = useState(preSelectedGXOption);
  const checked = !(
    selectedGeoOption === preSelectedGeoOption &&
    selectedGXOption === preSelectedGXOption
  );

  const [changed, setChanged] = useState(checked);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPrice, setNewPrice] = useState(
    selectedGeoOption && selectedGXOption
      ? comboOptions[selectedGeoOption]?.gxOptions[selectedGXOption]
      : undefined
  );

  const currentPrice =
    comboOptions[preSelectedGeoOption]?.gxOptions[preSelectedGXOption];

  const valueChanged = (type: string, value: string) => {
    if (type === 'geoAccess') {
      setSelectedGeoOption(value);
      const foundCombos = comboOptions[value]?.gxOptions
        ? Object.keys(comboOptions[value].gxOptions)
        : undefined;

      if (foundCombos?.length === 1 && foundCombos[0]) {
        setSelectedGXOption(foundCombos[0]);
      }
    } else {
      setSelectedGXOption(value);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (isSubmitting) {
      event.preventDefault();
    }
    setIsSubmitting(true);
  };

  useEffect(() => {
    setChanged(checked);
    setNewPrice(comboOptions[selectedGeoOption]?.gxOptions[selectedGXOption]);
  }, [selectedGeoOption, selectedGXOption]);

  return (
    <MyPageLayout {...myPageLayout}>
      {isSubmitting ? <Spinner theme={Spinner.themes.overlay} /> : null}
      <ContentContainer>
        <div className="change-membership-overview-page">
          <div className="change-membership-overview-page__content">
            <form
              action={endpoint}
              className="change-membership-option-page__content"
              method="POST"
              onSubmit={handleSubmit}
            >
              {hiddenInputs.map(input => (
                <HiddenInput key={input.name} {...input} />
              ))}
              <div className="change-membership-overview-page__membership-options">
                <div className="change-membership-overview-page__membership-option">
                  <Text
                    className="change-membership-overview-page__membership-option-title"
                    size={Text.sizes.headline2}
                    theme={Text.themes.emphasis}
                  >
                    {texts.geoTitle}
                  </Text>

                  {Object.entries(comboOptions).map(
                    ([geoKey, { geoCardDetails, gxOptions }]) => (
                      <RadioCard
                        {...geoCardDetails}
                        isChecked={geoKey === selectedGeoOption}
                        onClick={() =>
                          valueChanged(geoCardDetails.name, geoKey)
                        }
                      >
                        {geoKey === selectedGeoOption ? (
                          <>
                            <Text
                              className="change-membership-overview-page__membership-option-title"
                              size={Text.sizes.basic}
                              theme={Text.themes.headline}
                              italic={true}
                            >
                              {texts.gxTitle}
                            </Text>
                            {Object.entries(gxOptions).map(
                              ([gxKey, { gxCardDetails }]) => (
                                <RadioCard
                                  {...gxCardDetails}
                                  isChild
                                  isChecked={gxKey === selectedGXOption}
                                  onClick={() =>
                                    valueChanged(gxCardDetails.name, gxKey)
                                  }
                                ></RadioCard>
                              )
                            )}
                          </>
                        ) : null}
                      </RadioCard>
                    )
                  )}
                </div>
              </div>
              {nextButton ? (
                <div className="change-membership-overview-page__button-price-container">
                  <div className="change-membership-overview-page__inner">
                    <div>
                      <Text theme={Text.themes.normal} size={Text.sizes.large}>
                        {texts.currentPriceLabel +
                          ': ' +
                          (currentPrice
                            ? currentPrice.price + '/' + currentPrice.period
                            : '')}
                      </Text>
                      <Text
                        theme={Text.themes.emphasis}
                        size={Text.sizes.large}
                      >
                        {texts.newPriceLabel +
                          ': ' +
                          (newPrice
                            ? newPrice.price + '/' + newPrice.period
                            : '')}
                      </Text>
                    </div>
                    <span></span>
                    <Button
                      {...nextButton}
                      disabled={!changed || !newPrice}
                      className="change-membership-overview-page__desktop-button"
                      data-test-next
                    />
                  </div>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </ContentContainer>
    </MyPageLayout>
  );
};

export default ChangeMembershipOverviewPage;
